import type { CommitGeographyInput, DecodedRow } from './types';
import type { CreateSiteBody, PreviewRow, PreviewRowCells, SetActivityBody, UpdateSiteBody } from '../state/api/organisationEngineSlice';

/**
 * Single transform boundary: engine-parsed cells → typed request bodies.
 *
 * Pipelines downstream of this function never reach into raw `cells`, never
 * parse a string-as-float, and never special-case the blank-project-code →
 * `business_central_id: null` mapping. All of that lives here.
 *
 * Error rows yield `null` so the orchestrator can skip them with a single
 * filter instead of branching by status inside the per-row loop.
 */
export const decode = (row: PreviewRow): DecodedRow | null => {
    if (row.status === 'error') return null;
    if (row.status === 'new') {
        return {
            rowNumber: row.row,
            status: 'new',
            site: newSiteBody(row.cells),
            geography: geographyInput(row.cells),
            activity: activityBody(row.cells),
        };
    }
    // status === 'update' — only carry sub-objects whose underlying fields appear in `changes`.
    const siteFieldsChanged = row.changes.some(c => c === 'site_name' || c === 'project_code');
    const geographyChanged = row.changes.some(c => c === 'latitude' || c === 'longitude');
    const activityChanged = row.changes.includes('isic_code');

    return {
        rowNumber: row.row,
        status: 'update',
        internalId: row.cells.internal_id,
        site: siteFieldsChanged ? updateSiteBody(row.cells) : undefined,
        geography: geographyChanged ? geographyInput(row.cells) : undefined,
        activity: activityChanged ? activityBody(row.cells) : undefined,
        changes: row.changes,
    };
};

const newSiteBody = (cells: PreviewRowCells): CreateSiteBody => ({
    name: cells.site_name,
    // Blank project_code → omitted; engine leaves business_central_id null on create.
    business_central_id: cells.project_code === '' ? undefined : cells.project_code,
});

const updateSiteBody = (cells: PreviewRowCells): UpdateSiteBody => ({
    name: cells.site_name,
    // Blank project_code → null; engine clears business_central_id on update.
    business_central_id: cells.project_code === '' ? null : cells.project_code,
});

const geographyInput = (cells: PreviewRowCells): CommitGeographyInput => ({
    latitude: Number.parseFloat(cells.latitude),
    longitude: Number.parseFloat(cells.longitude),
});

const activityBody = (cells: PreviewRowCells): SetActivityBody => ({
    isic_code: cells.isic_code,
});
